/**
 * O campo dos formulários de Finanças: rótulo mono em caixa alta e o controle
 * embaixo.
 *
 * Papel: reproduz `.field`, `.field label`, `.field input/select/textarea`,
 * `.autofilled`, `.row2` e `.row3` (l. 122–130): 13 px abaixo de cada campo,
 * rótulo em mono 10 px 500 com `.5px`, controle de `8px 10px` 13 px com borda
 * bege, foco em `--foreground` com o anel de 3 px, e a seta do `select`
 * desenhada.
 *
 * Como usar: o controle é de quem monta, que lhe dá o `id` e a classe
 * `CLASSE_DO_CONTROLE` (ou `CLASSE_DO_SELECT`); o `Campo` só desenha o rótulo
 * ligado a ele por `htmlFor`, e um controle sem o mesmo `id` fica sem nome
 * acessível. O realce do autopreenchimento é `CLASSE_DO_AUTOPREENCHIDO`,
 * acrescentada ao controle enquanto ele não mudar; ela leva `!` porque a origem
 * leva `!important` e vence o foco. `LinhaDeCampos` não quebra em telas
 * estreitas: a origem não tem quebra para `.row2` e `.row3`.
 */
import { type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';

/** O `input` dos formulários. */
export const CLASSE_DO_CONTROLE =
  'border-emb-borda w-full appearance-none rounded-[6px] border bg-superficie px-2.5 py-2 font-[inherit] text-[13px] text-foreground outline-none transition-[border-color] duration-150 focus:border-foreground focus:ring-3 focus:ring-emb-foco';

/** O `select` dos formulários, com a seta da origem. */
export const CLASSE_DO_SELECT = `${CLASSE_DO_CONTROLE} bg-[image:var(--emb-seta-select)] bg-[position:right_10px_center] bg-no-repeat pr-[30px]`;

/** O realce do campo que o perfil do favorecido preencheu. */
export const CLASSE_DO_AUTOPREENCHIDO = '!border-emb-azul !bg-emb-autopreenchido';

/** O campo que só mostra: o Nome do perfil e a Categoria de "Nova Subcategoria". */
export const CLASSE_DE_SOMENTE_LEITURA = 'bg-background font-semibold text-texto-fraco';

/**
 * O campo fora de `.field`, com o contorno, a fonte e as cores do navegador: o
 * salário e os dias dos cartões de Outros Bancos e o valor das tabelas de
 * conferência. Em `readOnly` ele continua igual, só sem o cursor de texto.
 */
export const CLASSE_DO_CAMPO_SOLTO =
  '[border:revert] [border-radius:revert] [font:revert] [color:revert] [background-color:revert] read-only:cursor-default';

interface CampoProps {
  /** O texto do rótulo, como a origem o escreve; a caixa alta vem do CSS. */
  rotulo: ReactNode;
  /** O `id` do controle que vem em `children`. */
  id: string;
  children: ReactNode;
  /** Classes de quem chama, como a largura máxima de "Repetir por quantos meses?". */
  className?: string;
}

/**
 * Um campo com rótulo.
 *
 * @param rotulo - O rótulo.
 * @param id - O `id` do controle.
 * @param children - O controle e o que vier junto dele.
 * @param className - Classes de quem chama, aplicadas por último.
 * @returns O rótulo e o controle.
 */
export function Campo({ rotulo, id, children, className }: CampoProps) {
  return (
    <div className={cn('mb-[13px]', className)}>
      <label
        htmlFor={id}
        className="mb-1 block font-mono text-[10px] font-medium tracking-[0.5px] text-texto-fraco uppercase"
      >
        {rotulo}
      </label>
      {children}
    </div>
  );
}

/**
 * Dois ou três campos lado a lado.
 *
 * @param colunas - 2 para `.row2`, 3 para `.row3`.
 * @param children - Os `Campo`.
 * @param className - Classes de quem chama, como o `align-items:end` da linha
 *   de desconto.
 * @returns A grade de colunas iguais com 12 px entre elas.
 */
export function LinhaDeCampos({
  colunas,
  children,
  className,
}: {
  colunas: 2 | 3;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn('grid gap-3', colunas === 2 ? 'grid-cols-2' : 'grid-cols-3', className)}
    >
      {children}
    </div>
  );
}
